import React from 'react';
import { Calendar, MapPin, Briefcase, Award, ArrowRight } from 'lucide-react';

const Experience = () => {
  const experiences = [
    {
      title: "Consultant",
      company: "Infosys Limited",
      period: "December 2024 - September 2025",
      location: "Bhubaneswar, India",
      description: "Led strategic maintenance and production support for Kroger Pharmacy's Easyfill platform—a comprehensive Java application handling patient information management, managing prescription workflows, controlled substance compliance and reporting for retail pharmacy operations.",
      achievements: [
        "Pioneered Gen AI innovation by conceptualizing and developing a Gen AI chatbot POC that automated ticket resolution through database-driven workflow fixes and provided instant knowledge base access, reducing documentation search time and accelerating resolution time",
        "Led strategic maintenance and production support for Kroger Pharmacy's Easyfill platform handling patient information management, prescription workflows, and controlled substance compliance",
        "Drove operational excellence by identifying and implementing multiple automation solutions across CR deployment and delivery processes, optimizing processes, and reducing manual intervention for the support team"
      ],
      technologies: ["Gen AI", "Java", "Chatbot Development", "Process Automation", "Production Support", "Workflow Optimization"],
      current: false
    },
    {
      title: "Software Developer",
      company: "Amdocs Development Centre India LLP",
      period: "November 2021 - December 2024",
      location: "Pune, India",
      description: "Developed robust solutions enhancing customer experience on Amdocs CRM. Collaborated with global clients including Vodafone Romania, Bell Canada, Three Ireland, and XLCom Jakarta.",
      achievements: [
        "Developed secure PCI-compliant payment feature enabling CSRs to process payments via chat",
        "Integrated Amdocs CRM with third-party applications using Amdocs framework",
        "Reduced issue analysis time by 10 minutes through pre-analysis tool development",
        "Leveraged AI tools like GitHub Copilot for enhanced productivity",
        "Resolved multiple solution gaps and defects, enhancing user experience"
      ],
      technologies: ["Amdocs CRM", "Java", "REST APIs", "PCI Compliance", "GitHub Copilot", "System Integration"],
      current: false
    },
    {
      title: "Software Engineer",
      company: "Tech Mahindra Ltd (Client: Nestle)",
      period: "April 2018 - November 2021",
      location: "Bengaluru, India",
      description: "Developed automation tools and web applications for SAP PI environment, significantly reducing manual intervention and business disruptions.",
      achievements: [
        "Developed JAVA-based automation tool reducing manual resolution time by 90%",
        "Extended and maintained mass configuration web application reducing time by 70%",
        "Built incident tracking dashboard helping deliver solutions on time",
        "Implemented single-handed solutions from idea generation to maintenance"
      ],
      technologies: ["Java", "SAP PI", "Web Applications", "Automation", "ITSM", "System Integration"],
      current: false
    }
  ];

  const achievements = [
    {
      title: "Innovation Awards Winner",
      description: "Won awards for innovative ideas competition organized by P & C Department, GOVT. of Odisha (2015)",
      icon: <Award className="w-6 h-6" />
    },
    {
      title: "Best B.Tech Project",
      description: "Won Best B.Tech Project Competition in Simulation & Modeling category (2017)",
      icon: <Award className="w-6 h-6" />
    },
    {
      title: "Technical Publication",
      description: "Presented paper on 'FPGA Implementation of UART protocol with Flexible Baud Rate' (2017)",
      icon: <Briefcase className="w-6 h-6" />
    }
  ];

  return (
    <section id="experience" className="py-12 sm:py-16 lg:py-20 bg-slate-900">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Work <span className="text-blue-400">Experience</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-500 mx-auto mb-6 sm:mb-8"></div>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">
            6+ years of comprehensive IT experience across development, automation, and system optimization
          </p>
        </div>

        {/* Experience Cards - Clean Layout */}
        <div className="max-w-4xl mx-auto space-y-8 lg:space-y-12">
          {experiences.map((exp, index) => (
            <div key={index} className="w-full">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
                {/* Header */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl md:text-2xl font-bold text-white text-left">
                      {exp.title}
                    </h3>
                    {exp.current && (
                      <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs font-medium border border-green-500/30">
                        Current
                      </span>
                    )}
                  </div>
                  <h4 className="text-lg md:text-xl text-blue-400 font-semibold mb-2 text-left">
                    {exp.company}
                  </h4>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-gray-400 text-sm">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {exp.period}
                    </div>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {exp.location}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-300 mb-4 leading-relaxed text-left">
                  {exp.description}
                </p>

                {/* Key Achievements */}
                <div className="mb-4">
                  <h5 className="text-white font-semibold mb-3 flex items-center text-left">
                    <ArrowRight className="w-4 h-4 mr-2 text-blue-400" />
                    Key Achievements
                  </h5>
                  <ul className="space-y-2">
                    {exp.achievements.map((achievement, achIndex) => (
                      <li key={achIndex} className="text-gray-400 text-sm flex items-start text-left">
                        <span className="text-blue-400 mr-2 mt-1.5 text-xs">▪</span>
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Technologies */}
                <div>
                  <h5 className="text-white font-semibold mb-3 text-left">Technologies Used</h5>
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="bg-slate-700/50 text-blue-400 px-3 py-1 rounded-full text-xs border border-blue-500/30 hover:bg-blue-500/20 transition-all duration-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Career Highlights */}
        <div className="mt-16">
          <h3 className="text-2xl md:text-3xl font-semibold text-white mb-8 text-center">
            Career <span className="text-blue-400">Highlights</span>
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center hover:border-blue-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10"
              >
                <div className="text-blue-400 mb-4 flex justify-center">
                  {achievement.icon}
                </div>
                <h4 className="text-lg font-semibold text-white mb-3">
                  {achievement.title}
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Career Stats */}
        <div className="mt-16 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-blue-400 mb-2">6+</div>
              <p className="text-gray-400 text-sm">Years Experience</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-purple-400 mb-2">3</div>
              <p className="text-gray-400 text-sm">Companies</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2">5+</div>
              <p className="text-gray-400 text-sm">Global Clients</p>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">90%</div>
              <p className="text-gray-400 text-sm">Automation Impact</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;